<template
>
  <div class="detail-page">
    <van-nav-bar title="任务详情" left-arrow @click-left="$router.back()"/>
    <main v-if="task">
      <header>
        <em :class="task.assignmentType.toLowerCase()">{{
            task.assignmentTypeLabel
          }}</em
        ><span :class="task.status.toLowerCase()">{{
          status(task.status)
        }}</span>
      </header>
      <h1>{{ task.title }}</h1>
      <p v-if="!isProject" class="from">
        由 {{ task.assignerName }} 下发 · 绩效权重 {{ task.performanceWeight }}%
      </p>
      <p v-else class="from">
        项目任务 · 当前完成率 {{ task.completionRate || 0 }}%
      </p>
      <section v-if="!isProject" class="flow">
        <div
          v-for="(step, index) in flowSteps"
          :key="step"
          :class="{ done: index < flowIndex, current: index === flowIndex }"
        >
          <i>{{ index < flowIndex ? "✓" : index + 1 }}</i><span>{{ step }}</span>
        </div>
      </section>
      <p v-if="task.status === 'RETURNED'" class="return-tip">
        <van-icon name="replay"/>
        任务已被退回，请根据验收反馈修改后重新开始。
      </p>
      <section>
        <h2>{{ isProject ? '任务目标' : '任务内容' }}</h2>
        <p>{{ task.content }}</p>
      </section>
      <section>
        <h2>验收标准</h2>
        <p>{{ task.acceptanceCriteria }}</p>
      </section>
      <section class="time">
        <div>
          <span>开始时间</span><b>{{ date(task.startAt) }}</b>
        </div>
        <div>
          <span>截止时间</span
          ><b :class="{ overdue: task.overdue }">{{ date(task.dueAt) }}</b>
        </div>
      </section>
      <section v-if="isProject && task.subtasks && task.subtasks.length" class="subtasks">
        <h2>子任务</h2>
        <div v-for="sub in task.subtasks" :key="sub.id" class="subtask-item">
          <b>{{ sub.title }}</b>
          <span>{{ subStatus(sub.status) }} · {{ sub.completion_rate || sub.completionRate || 0 }}%</span>
        </div>
      </section>
      <section v-if="task.reviewNote" class="review">
        <h2>验收反馈</h2>
        <p>{{ task.reviewNote }}</p>
        <strong v-if="task.score != null">{{ task.score }} 分</strong>
      </section>
      <div
        v-if="!isProject && (task.status === 'IN_PROGRESS' || task.status === 'RETURNED')"
        class="submit-form"
      >
        <label
        >完成说明<textarea
          v-model.trim="completionNote"
          maxlength="3000"
          placeholder="填写实际完成情况和关键成果"/></label
        ><label
      >成果凭证（选填）<input
        v-model.trim="evidenceReference"
        maxlength="1000"
        placeholder="链接、文件编号或存放位置"
      /></label>
      </div>
    </main>
    <footer v-if="task && isProject" class="actions">
      <van-button block color="#B71C1C" @click="$router.push({name:'EfficiencyWorkPoints'})">去生成今日工作要点</van-button>
    </footer>
    <footer v-if="task && !isProject" class="actions">
      <van-button
        v-if="task.status === 'ASSIGNED' || task.status === 'RETURNED'"
        block
        color="#B71C1C"
        :loading="saving"
        @click="act('START')"
      >{{ task.status === "RETURNED" ? "重新开始" : "开始任务" }}
      </van-button
      >
      <van-button
        v-if="task.status === 'IN_PROGRESS'"
        block
        color="#B71C1C"
        :loading="saving"
        @click="act('SUBMIT')"
      >提交成果
      </van-button
      >
      <p v-if="task.status === 'SUBMITTED'">成果已提交，等待 Boss 验收</p>
      <p v-if="task.status === 'VERIFIED'">任务已验收并计入绩效</p>
    </footer>
  </div>
</template
>
<script>
/**
 * 员工侧重大/临时任务详情：开始 → 提交成果 → Boss 验收的状态流。
 */
import coreapi from "@/utils/coreapi";

export default {
  data() {
    return {
      task: null,
      completionNote: "",
      evidenceReference: "",
      saving: false,
      flowSteps: ["待开始", "进行中", "待验收", "已完成"]
    };
  },
  computed: {
    isProject() {
      return this.$route.query.kind === "project";
    },
    // RETURNED 与 IN_PROGRESS 同属「进行中」步骤
    flowIndex() {
      return {
        ASSIGNED: 0,
        IN_PROGRESS: 1,
        RETURNED: 1,
        SUBMITTED: 2,
        VERIFIED: 3
      }[this.task ? this.task.status : "ASSIGNED"];
    }
  },
  mounted() {
    this.load();
  },
  methods: {
    field(row, key) {
      if (!row) return "";
      return row[key] !== undefined ? row[key] : row[key.toUpperCase()];
    },
    mapProjectTask(row) {
      return {
        id: this.field(row, "id"),
        assignmentType: "PROJECT",
        assignmentTypeLabel: "项目任务",
        title: this.field(row, "title"),
        content: this.field(row, "objective") || this.field(row, "description") || "",
        acceptanceCriteria: this.field(row, "acceptance_criteria") || "",
        startAt: this.field(row, "start_at"),
        dueAt: this.field(row, "due_at") || this.field(row, "end_at"),
        status: this.field(row, "status") || "ASSIGNED",
        completionRate: this.field(row, "completion_rate") || 0,
        subtasks: row.subtasks || row.SUBTASKS || [],
        overdue: false
      };
    },
    async load() {
      try {
        if (this.isProject) {
          this.task = this.mapProjectTask(await coreapi.projectTask(this.$route.params.id));
          return;
        }
        this.task = await coreapi.task(this.$route.params.id);
        this.completionNote = this.task.completionNote || "";
        this.evidenceReference = this.task.evidenceReference || "";
      } catch (e) {
        this.$toast.fail(e.message);
      }
    },
    async act(action) {
      if (action === "SUBMIT" && !this.completionNote)
        return this.$toast("请填写完成说明");
      if (this.saving) return;
      this.saving = true;
      try {
        // 同一接口处理 START / SUBMIT，返回最新任务快照
        this.task = await coreapi.taskAction(this.task.id, {
          action,
          completionNote: this.completionNote,
          evidenceReference: this.evidenceReference
        });
        this.$toast.success(action === "START" ? "任务已开始" : "成果已提交");
      } catch (e) {
        this.$toast.fail(e.message);
      } finally {
        this.saving = false;
      }
    },
    date(v) {
      return v ? new Date(v).toLocaleString("zh-CN") : "--";
    },
    subStatus(v) {
      return ({ASSIGNED: "待开始", IN_PROGRESS: "进行中", DONE: "已完成"})[v] || v;
    },
    status(v) {
      return (
        {
          ASSIGNED: "待开始",
          IN_PROGRESS: "进行中",
          SUBMITTED: "待验收",
          VERIFIED: "已完成",
          RETURNED: "已退回"
        }[v] || v
      );
    }
  }
};
</script>
<style lang="less" scoped>
@import "~@/assets/base.less";

.detail-page {
  min-height: 100vh;
  padding-bottom: 2.65rem;
  background: #f6f7fb;
}

main {
  margin: 0.28rem;
  padding: 0.34rem;
  background: #fff;
  border-radius: 0.24rem;
  box-shadow: 0 0.08rem 0.3rem rgba(211, 47, 47, 0.1);
}

main > header {
  display: flex;
  justify-content: space-between;
}

em {
  padding: 0.06rem 0.14rem;
  color: @color;
  font-size: 0.23rem;
  font-style: normal;
  background: #fff5f5;
  border-radius: 0.24rem;
}

em.project {
  color: #1565c0;
  background: #eef5ff;
}

.subtask-item {
  padding: 0.16rem 0;
  border-top: 1px solid #f0f0f0;
  font-size: 0.27rem;
}

.subtask-item span {
  display: block;
  margin-top: 0.06rem;
  color: #9295a7;
  font-size: 0.23rem;
}

em.major {
  color: #a66b10;
  background: #fff3df;
}

header span {
  font-size: 0.25rem;
}

.submitted {
  color: #e48b18;
}

.verified {
  color: #2d9b61;
}

.returned,
.overdue {
  color: #D32F2F;
}

h1 {
  margin: 0.25rem 0 0.08rem;
  font-size: 0.43rem;
}

.from {
  color: #898c9e;
  font-size: 0.25rem;
}

.flow {
  padding: 0.3rem 0 0.08rem;
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
}

.flow div {
  position: relative;
  min-width: 0;
  text-align: center;
  color: #a0a2b1;
  font-size: 0.22rem;
}

.flow div::before {
  content: "";
  position: absolute;
  z-index: 0;
  top: 0.26rem;
  right: 50%;
  width: 100%;
  height: 2px;
  background: #e6e7ef;
}

.flow div:first-child::before {
  display: none;
}

.flow i {
  position: relative;
  z-index: 1;
  width: 0.52rem;
  height: 0.52rem;
  margin: 0 auto 0.12rem;
  display: flex;
  align-items: center;
  justify-content: center;
  border: 2px solid #e0e1ea;
  border-radius: 50%;
  background: #fff;
  font-size: 0.21rem;
  font-style: normal;
  box-sizing: border-box;
}

.flow div.done,
.flow div.current {
  color: #B71C1C;
}

.flow div.done::before,
.flow div.current::before {
  background: #9ba0ea;
}

.flow div.done i,
.flow div.current i {
  color: #fff;
  border-color: #B71C1C;
  background: #B71C1C;
}

.flow div.current i {
  box-shadow: 0 0 0 0.08rem #eff0ff;
}

.return-tip {
  margin-top: 0.22rem;
  padding: 0.18rem 0.2rem;
  color: #b45454;
  border-radius: 0.12rem;
  background: #fff2f2;
  font-size: 0.24rem;
  line-height: 1.5;
}

.return-tip .van-icon {
  margin-right: 0.08rem;
}

section {
  margin-top: 0.3rem;
  padding-top: 0.28rem;
  border-top: 1px solid #eee;
}

section h2 {
  margin-bottom: 0.12rem;
  font-size: 0.31rem;
}

section p {
  color: #55586b;
  font-size: 0.28rem;
  line-height: 1.65;
  white-space: pre-wrap;
}

.time {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 0.18rem;
}

.time span,
.time b {
  display: block;
}

.time span {
  color: #9295a7;
  font-size: 0.23rem;
}

.time b {
  margin-top: 0.08rem;
  font-size: 0.27rem;
}

.review {
  position: relative;
  padding: 0.24rem;
  background: #fff5f5;
  border: 0;
  border-radius: 0.16rem;
}

.review strong {
  position: absolute;
  right: 0.22rem;
  top: 0.22rem;
  color: @color;
  font-size: 0.38rem;
}

.submit-form {
  margin-top: 0.3rem;
}

.submit-form label {
  display: block;
  margin-top: 0.2rem;
  color: #55586b;
  font-size: 0.27rem;
}

.submit-form textarea,
.submit-form input {
  width: 100%;
  margin-top: 0.1rem;
  padding: 0.2rem;
  border: 1px solid #e2e3ec;
  border-radius: 0.12rem;
  background: #fafaff;
  box-sizing: border-box;
}

.submit-form textarea {
  height: 1.5rem;
  resize: none;
}

.actions {
  position: fixed;
  z-index: 5;
  left: 0;
  right: 0;
  bottom: 50px;
  padding: 0.22rem 0.28rem;
  background: #fff;
  box-shadow: 0 -0.04rem 0.2rem rgba(0, 0, 0, 0.07);
}

.actions .van-button {
  height: 0.84rem;
  border-radius: 0.14rem;
  box-shadow: 0 0.08rem 0.2rem rgba(78, 84, 199, 0.2);
  font-size: 0.3rem;
  font-weight: 500;
  letter-spacing: 0;
}

.actions p {
  text-align: center;
  color: #777b90;
  font-size: 0.27rem;
}
</style>



// WEBPACK FOOTER //
// src/views/efficiency/TaskDetail.vue